import { consoleIfNotProd } from 'chatspot-core/loggers/consoleIfNotProd';
// Stolen from chatspot-ui
export const safeParse = data => {
  let parsedData;
  try {
    parsedData = JSON.parse(data);
  } catch (error) {
    // noop
  }
  return parsedData;
};
export const MAX_DEPTH = 2;
export const MAX_DESCRIPTION_LENGTH = 1024;

/**
 * Detects the JSON Schema type of a value
 * @param value - The value to detect the type of
 * @returns The JSON Schema type
 * @throws Error if the type is unsupported
 */
export function detectType(value) {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  const typeOfValue = typeof value;
  switch (typeOfValue) {
    case 'string':
    case 'number':
    case 'boolean':
      return typeOfValue;
    case 'object':
      // At this point, we know it's an object and not null or array
      return 'object';
    default:
      throw new Error(`Unsupported type: ${typeOfValue}`);
  }
}

/**
 * Type guard to check if a SchemaValue is an ObjectSchemaValue
 */
export function isObjectSchemaValue(value) {
  const type = value.type || (value.currentValue !== undefined ? detectType(value.currentValue) : null);
  return type === 'object';
}

/**
 * Type guard to check if a SchemaValue is an ArraySchemaValue
 */
export function isArraySchemaValue(value) {
  const type = value.type || (value.currentValue !== undefined ? detectType(value.currentValue) : null);
  return type === 'array';
}

/**
 * Creates a JSON Schema property from a SchemaValue
 * @param value - The SchemaValue to convert
 * @param path - The path to use for the schema ID
 * @param depth - Current nesting depth (default: 0)
 * @returns A JSON Schema property
 * @throws Error if depth exceeds MAX_DEPTH levels
 */
export function createValueSchema(value, paramName, depth = 0) {
  if (depth > MAX_DEPTH) {
    throw new Error(`Schema nesting depth exceeds maximum of ${MAX_DEPTH} levels`);
  }

  // Use the explicit type if provided, otherwise detect from currentValue
  const type = value.type || (value.currentValue !== undefined ? detectType(value.currentValue) : 'string');
  if (value !== null && value !== void 0 && value.description && (value === null || value === void 0 ? void 0 : value.description.length) > MAX_DESCRIPTION_LENGTH) {
    throw new Error(`property ${paramName} has a description that is too longer than ${MAX_DESCRIPTION_LENGTH} characters. Please update in order to use interactive mode.`);
  }
  const description = value.description;
  if (!description) {
    throw new Error(`property ${paramName} is missing the description field. Please update in order to use interactive mode.`);
  }
  const schema = {
    type,
    description
  };

  // Handle regular enums
  if (value.enum) {
    schema.enum = value.enum;
  }

  // Handle pattern validation
  if (value.type === 'string' && 'pattern' in value && value.pattern) {
    schema.pattern = value.pattern;
  }
  schema.description = description;
  // Handle possible values as a nested schema
  if (value.possibleValues) {
    var _value$possibleValues;
    const firstValue = (_value$possibleValues = value.possibleValues[0]) === null || _value$possibleValues === void 0 ? void 0 : _value$possibleValues.value;
    schema.type = typeof firstValue === 'number' ? 'number' : 'string';
    schema.enum = value.possibleValues.map(pv => pv.value);
    schema.description += `\nPossible values: ${value.possibleValues.map(pv => `${pv.label}: ${pv.value}`).join(', ')}`;
  }
  if (isObjectSchemaValue(value) && 'properties' in value) {
    schema.properties = {};
    schema.required = [];
    schema.additionalProperties = false;
    Object.entries(value.properties).forEach(([propName, propValue]) => {
      schema.properties[propName] = createValueSchema(propValue, propName, depth + 1);
      // OpenAI structured output requires ALL nested properties to be required
      // Individual 'required' settings on nested properties are ignored
      schema.required.push(propName);
    });

    // Always keep the required array for OpenAI structured output compliance
  }
  if (isArraySchemaValue(value) && 'items' in value) {
    schema.items = createValueSchema(value.items, `${paramName}Item`, depth + 1);
  }
  return schema;
}

/**
 * Creates a JSON Schema for validating function outputs
 * @param interactions - The interactions to create a schema for
 * @returns A JSON Schema for validating function outputs
 */

const RESERVED_PARAMETER_NAMES = ['functionName', 'showAsButton', 'buttonText', 'buttonExplanation', 'buttonHeader'];

/**
 * Appends reserved schema properties to an interaction schema
 * @param functionDef - The function definition containing name, description, and confirmation requirements
 * @returns Object with reserved properties for the schema
 */
function appendReservedSchema(functionDef) {
  const reservedProperties = {
    functionName: {
      type: 'string',
      const: functionDef.name,
      description: functionDef.functionDescription
    },
    showAsButton: {
      type: 'boolean',
      description: 'this value indicates whether the action should be shown as a button (e.g. suggestions, unclear intent); false if the user gave a direct request'
    },
    buttonText: {
      type: 'string',
      description: 'REQUIRED (even if showAsButton is false): The text to display on the button for this action. e.g. "Update section"',
      pattern: '^.{5,30}$'
    },
    buttonExplanation: {
      type: 'string',
      description: 'REQUIRED (even if showAsButton is false): This description is shown to the user to explain the action that was either performed or will be performed when the user clicks the button. Should be a clear and concise description of the action that was performed or will be performed.',
      pattern: '^.{50,100}$'
    },
    buttonHeader: {
      type: 'string',
      description: 'REQUIRED (even if showAsButton is false): A short explanation of the action that was performed or will be performed when the user clicks the button. e.g. "The section has been updated to be be in a friendlier tone.", "The section has been removed.", "Apply the changes to the section."',
      pattern: '^.{10,25}$'
    }
  };

  // If confirmation is required, show it as a button regardless of user request
  if (functionDef.requiresUserConfirmation) {
    reservedProperties.showAsButton.const = true;
    reservedProperties.showAsButton.description = 'IMPORTANT: this must be true regardless of whether the user requests the change or not.';
  }
  return reservedProperties;
}
export function feInteractionsToFrontEndTools(interactions) {
  // Return empty array if interactions is undefined, null, or not an array
  if (!Array.isArray(interactions)) {
    return [];
  }
  const tools = [];

  // Process each interaction to create its individual schema
  interactions.forEach(functionDef => {
    if (!(functionDef !== null && functionDef !== void 0 && functionDef.name)) {
      consoleIfNotProd.warn('Interaction is missing a name, skipping interaction');
      return;
    }

    // Create a new schema for this specific interaction
    const interactionSchema = {
      type: 'object',
      properties: appendReservedSchema(functionDef),
      description: functionDef.functionDescription,
      required: [...RESERVED_PARAMETER_NAMES],
      additionalProperties: false
    };

    // Try to process all function parameters - if any exceed depth limit, skip entire interaction
    let skipThisInteraction = false;
    if (functionDef.functionParameters && interactionSchema.properties) {
      try {
        for (const [paramName, paramValue] of Object.entries(functionDef.functionParameters)) {
          if (!paramValue) continue;

          // Prevent functionName from being used as a parameter name
          if (RESERVED_PARAMETER_NAMES.includes(paramName)) {
            consoleIfNotProd.warn(`Parameter name '${paramName}' is reserved and cannot be used in function '${functionDef.name}', skipping interaction`);
            skipThisInteraction = true;
            break;
          }
          const nestedSchema = createValueSchema(paramValue, paramName, 0);
          interactionSchema.properties[paramName] = nestedSchema;

          // OpenAI structured output requires ALL properties to be in required array
          interactionSchema.required.push(paramName);
        }
      } catch (error) {
        // Handle all errors (depth limit, missing descriptions, etc.)
        consoleIfNotProd.warn(`Error in function '${functionDef.name}': ${error instanceof Error ? error.message : 'Unknown error'}, skipping interaction`);
        skipThisInteraction = true;
      }
    }

    // Only add this tool if it didn't exceed depth limit or have other errors
    if (!skipThisInteraction) {
      tools.push({
        name: functionDef.name,
        dependentTools: functionDef.dependentTools || [],
        schema: JSON.stringify(interactionSchema)
      });
    }
  });
  return tools;
}

// -----------------------------------------------------------------------------
// An individual action returned by the LLM when using the schema generated by
// `feInteractionsToFrontEndTools`.
//
// Shape rules:
// 1. `functionName` MUST be included and MUST match one of the `name` values
//    supplied in `interactions` when building the schema.
// 2. Every other enumerable key (except the auxiliary keys below) is treated as
//    a parameter to the consumer-provided function identified by
//    `functionName`. These parameters therefore use `unknown` – their concrete
//    types depend on the function definition that created the schema.
// 3. The LLM can optionally include UI-hints that the frontend interprets when
//    rendering the action.
//    • `showAsButton` – when `true` the action will be rendered as a button and
//      executed only after user confirmation.
//    • `buttonText`  – the label to display on the button when `showAsButton`
//      is `true`.
// -----------------------------------------------------------------------------
export const getExtractedParameterNames = action => {
  return Object.keys(action).reduce((params, key) => {
    if (!RESERVED_PARAMETER_NAMES.includes(key)) {
      params[key] = action[key];
    }
    return params;
  }, {});
};